/**
 * Cache Content Dialog Component
 *
 * Displays the actual cache value in a modal dialog with JSON formatting.
 */

import { Loader2, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCacheValue } from '@/hooks/use-cache-management';

interface CacheContentDialogProps {
  cacheKey: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Format JSON value for display
 */
function formatValue(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return 'undefined';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Get size of the cached value in a human-readable format
 */
function getValueSize(value: unknown): string {
  try {
    const str = JSON.stringify(value);
    const bytes = new Blob([str]).size;
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  } catch {
    return 'N/A';
  }
}

/**
 * Cache Content Dialog
 * Displays the actual content of a cached value
 */
export function CacheContentDialog({ cacheKey, open, onOpenChange }: CacheContentDialogProps) {
  const { data, isLoading, error } = useCacheValue(open ? cacheKey : null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (data?.value !== null && data?.value !== undefined) {
      try {
        await navigator.clipboard.writeText(formatValue(data.value));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Clipboard API may not be available
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Cache Content
            {data?.value !== undefined && data?.value !== null && (
              <Badge variant="outline" className="ml-2 text-xs font-normal">
                {getValueSize(data.value)}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs truncate" title={cacheKey || ''}>
            {cacheKey}
          </DialogDescription>
        </DialogHeader>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          {/* Copy Button */}
          {data?.value !== undefined && data?.value !== null && (
            <Button
              variant="outline"
              size="sm"
              className="absolute right-2 top-2 z-10"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          )}

          <ScrollArea className="h-[400px] w-full rounded-md border bg-muted/30">
            <div className="p-4">
              {isLoading && (
                <div className="flex items-center justify-center h-[368px]">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading cache value...</span>
                </div>
              )}

              {error && (
                <div className="flex flex-col items-center justify-center h-[368px] text-destructive">
                  <p className="font-medium">Failed to load cache value</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {error instanceof Error ? error.message : 'Unknown error'}
                  </p>
                </div>
              )}

              {!isLoading && !error && data?.value === null && (
                <div className="flex flex-col items-center justify-center h-[368px] text-muted-foreground">
                  <p className="font-medium">Cache key not found</p>
                  <p className="text-sm mt-1">
                    The cache entry may have expired or been invalidated.
                  </p>
                </div>
              )}

              {!isLoading && !error && data?.value !== null && data?.value !== undefined && (
                <pre className="text-sm font-mono whitespace-pre-wrap break-all overflow-hidden">
                  {formatValue(data.value)}
                </pre>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
